/**
 * Per-atlas QFV ("portion of region injured" probability) and lesionload
 * (raw per-ROI overlap voxel count) CSV export, matching the real pipeline's
 * `reporting_qfv_calculation.py`/`reporting_pwi_qfv_calculation.py`
 * `StrokeQFVCalculator.save_QFV_to_csv` file-for-file, for the 3 atlases
 * this app bundles (vascular, lobe, aspects — see `atlas.ts`'s own
 * documented scope cut for why not all ~9 real atlases are wired).
 *
 * IMPORTANT, found and fixed during this pass's own numeric verification
 * (don't "simplify" this back, it was wrong once already): the real QFV
 * combine is a TWO-STEP "probability of each side, then sum the two
 * probabilities" (`vec_VascAtlas2visual`/`vec_LobeAtlas2visual` producing a
 * 16/21-element L/R-separated vector, divided by the same-shape volume
 * vector, THEN `get_Vas_visual_prob_comb` et al. do `prob[0::2]+prob[1::2]`)
 * — NOT "sum both sides' overlap, sum both sides' volume, then divide"
 * (which is what `interpretation.ts`'s `vascularVisual8`/`lobeVisual11`/
 * `aspectsVisual10` do, correctly, for ITS OWN purpose — those feed the
 * RandomForest models' mL-valued features, a real, independently-verified,
 * genuinely different computation; do not reuse them here). The two
 * orderings are NOT equivalent except when a region's lesion overlap is
 * symmetric across its L/R pair — confirmed by hand against a real native
 * CSV (`.../PWI/reporting/sub-02e8eb42_Vascular_HPQFV.csv`): the naive
 * ratio-of-sums gave ~1.8-2.0x too-small values for this (left-lesion)
 * subject; the real sum-of-ratios reproduces the native numbers.
 *
 * SECOND bug found and fixed, same CSV: the `volml` column is misleadingly
 * named — it is NOT the linear mL volume. The real source
 * (`reporting_qfv_calculation.py:253`, `StrokeQFVCalculator.calculate`)
 * computes `stroke_logml = log10((np.sum(stroke_img)+1)/1000)` and writes
 * THAT into the column `get_QFV` later names `volml` — a log10-mL value,
 * passed straight through with no further transform (`get_QFV`'s
 * `np.append(predict_vol_logml, combined)`, `reporting_qfv_calculation.py`
 * ~L233). Confirmed numerically: native's real value for this subject is
 * 2.4345242; `log10(totalMl + 0.001)` for this app's own computed totalMl
 * (267.158) is ≈2.4268 — a ~1.8% gap in the underlying linear mL, well
 * inside this project's established WASM-vs-native segmentation variance,
 * not a formula error. `+0.001` (not `+1`) because `totalMl` here is
 * already voxel_count × voxelVolumeMl(pixdim) — i.e. already divided by
 * 1000 for this pipeline's 1mm-isotropic MNI grid — so native's
 * `(voxel_count+1)/1000` becomes `totalMl + 0.001`, not `totalMl + 1`.
 */
function volmlColumn(totalMl: number): number {
  return round7(Math.log10(totalMl + 0.001));
}
import type { RegionStat, VentricleFeatures } from "./atlas";

function byName(stats: RegionStat[], pick: (s: RegionStat) => number): Map<string, number> {
  const m = new Map<string, number>();
  for (const s of stats) m.set(s.region, pick(s));
  return m;
}

function csvRow(cells: (string | number)[]): string {
  return cells
    .map((c) => (typeof c === "number" ? c : /[",\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c))
    .join(",");
}

function round7(v: number): number {
  return Math.round(v * 1e7) / 1e7;
}

/** `vec_VascAtlas2visual`: real 16-element L/R-separated remap, ported exact. */
function vascularVisual16(v: (name: string) => number): number[] {
  return [
    v("ACAL") + v("MLSL"), v("ACAR") + v("MLSR"),
    v("MCAFL") + v("MCAPL") + v("MCATL") + v("MCAOL") + v("MCAIL"),
    v("MCAFR") + v("MCAPR") + v("MCATR") + v("MCAOR") + v("MCAIR"),
    v("PCATL") + v("PCAOL"), v("PCATR") + v("PCAOR"),
    v("SCL") + v("ICL"), v("SCR") + v("ICR"),
    v("BL"), v("BR"),
    v("LLSL"), v("LLSR"),
    v("PCTPL") + v("ACTPL"), v("PCTPR") + v("ACTPR"),
    v("LVL"), v("LVR"),
  ];
}

/** `vec_LobeAtlas2visual`: real 21-element L/R-separated remap, ported exact. */
function lobeVisual21(v: (name: string) => number): number[] {
  return [
    v("BasalGanglia_L"), v("BasalGanglia_R"),
    v("CSO_L") + v("CorRad_L"), v("CSO_R") + v("CorRad_R"),
    v("cerebellum_L"), v("cerebellum_R"),
    v("frontal_L"), v("frontal_R"),
    v("insula_L"), v("insula_R"),
    v("IntCapsule_L"), v("IntCapsule_R"),
    v("midbrain") + v("pons") + v("medulla"),
    v("occipital_L"), v("occipital_R"),
    v("parietal_L"), v("parietal_R"),
    v("temporal_L"), v("temporal_R"),
    v("Thalamus_L"), v("Thalamus_R"),
  ];
}

function pairwiseSum(prob: number[]): number[] {
  const out: number[] = [];
  for (let i = 0; i + 1 < prob.length; i += 2) out.push(prob[i] + prob[i + 1]);
  return out;
}

/** `get_Lobe_visual_prob_comb`: standard pairwise EXCEPT index 6 (brainstem,
 *  a single already-combined value, doubled instead of paired — the real
 *  function's own asymmetry, reproduced exactly). */
function lobeProbComb(prob21: number[]): number[] {
  const out = new Array<number>(11);
  for (let i = 0; i < 6; i++) out[i] = prob21[i * 2] + prob21[i * 2 + 1];
  out[6] = prob21[12] * 2; // brainstem
  for (let i = 0; i < 4; i++) out[7 + i] = prob21[13 + i * 2] + prob21[13 + i * 2 + 1];
  return out;
}

/** Real per-visual-ROI probability: sum overlap WITHIN one side across all
 *  constituent raw regions, sum volume the same way, THEN divide — a ratio
 *  of (same-side) sums, not a sum of per-raw-region ratios (confirmed by
 *  hand against a real native CSV; a same-side-sum-of-ratios variant was
 *  tried first and overshot by ~5x on multi-region categories like MCA). */
function visualProb(
  stats: RegionStat[],
  combine: (v: (name: string) => number) => number[],
): number[] {
  const overlapByName = byName(stats, (s) => s.overlapVoxels);
  const volumeByName = byName(stats, (s) => s.regionTotalVoxels);
  const overlapVisual = combine((n) => overlapByName.get(n) ?? 0);
  const volumeVisual = combine((n) => volumeByName.get(n) ?? 0);
  return overlapVisual.map((o, i) => o / (volumeVisual[i] + 1e-6));
}

export interface QfvCsvSet {
  qfvCsv: string;
  lesionloadCsv: string;
}

const VAS_QFV_NAMES = ["ACA", "MCA", "PCA", "cerebellar", "basilar", "Lenticulostriate", "Choroidal & Thalamoperfurating", "watershed"];
const LOBE_QFV_NAMES = ["basal ganglia", "deep white matter", "cerebellum", "frontal", "insula", "internal capsule", "brainstem", "occipital", "parietal", "temporal", "thalamus"];
const ASPECT_PAIRS: [string, string][] = [
  ["caudate_L", "caudate_R"], ["lentiform_L", "lentiform_R"], ["intern_capsule_L", "intern_capsule_R"],
  ["insula_L", "insula_R"], ["M1_L", "M1_R"], ["M2_L", "M2_R"], ["M3_L", "M3_R"],
  ["M6_L", "M6_R"], ["M5_L", "M5_R"], ["M4_L", "M4_R"],
];
const ASPECT_QFV_NAMES = ["Caudate", "lentiform", "IC", "insula", "M1", "M2", "M3", "M6", "M5", "M4"];

function lesionload(subjectId: string, stats: RegionStat[]): string {
  void subjectId;
  const header = csvRow(stats.map((s) => s.region));
  const row = csvRow(stats.map((s) => s.overlapVoxels));
  return `${header}\n${row}\n`;
}

export function buildVascularQfv(subjectId: string, stats: RegionStat[], totalMl: number): QfvCsvSet {
  const prob16 = visualProb(stats, vascularVisual16);
  const combined8 = pairwiseSum(prob16);
  const qfvCsv = `${csvRow(["subject_id", "volml", ...VAS_QFV_NAMES])}\n${csvRow([subjectId, volmlColumn(totalMl), ...combined8.map(round7)])}\n`;
  return { qfvCsv, lesionloadCsv: lesionload(subjectId, stats) };
}

export function buildLobeQfv(subjectId: string, stats: RegionStat[], totalMl: number): QfvCsvSet {
  const prob21 = visualProb(stats, lobeVisual21);
  const combined11 = lobeProbComb(prob21);
  const qfvCsv = `${csvRow(["subject_id", "volml", ...LOBE_QFV_NAMES])}\n${csvRow([subjectId, volmlColumn(totalMl), ...combined11.map(round7)])}\n`;
  return { qfvCsv, lesionloadCsv: lesionload(subjectId, stats) };
}

export function buildAspectsQfv(subjectId: string, stats: RegionStat[], totalMl: number): QfvCsvSet {
  const overlapByName = byName(stats, (s) => s.overlapVoxels);
  const volumeByName = byName(stats, (s) => s.regionTotalVoxels);
  const prob = (name: string) => (overlapByName.get(name) ?? 0) / ((volumeByName.get(name) ?? 0) + 1e-6);
  const combined10 = ASPECT_PAIRS.map(([l, r]) => prob(l) + prob(r));
  const qfvCsv = `${csvRow(["subject_id", "volml", ...ASPECT_QFV_NAMES])}\n${csvRow([subjectId, volmlColumn(totalMl), ...combined10.map(round7)])}\n`;
  return { qfvCsv, lesionloadCsv: lesionload(subjectId, stats) };
}

export function buildAllQfvCsvs(
  subjectId: string,
  atlasStats: Record<string, RegionStat[]>,
  totalMl: number,
): { vascular: QfvCsvSet; lobe: QfvCsvSet; aspects: QfvCsvSet } {
  return {
    vascular: buildVascularQfv(subjectId, atlasStats.vascular ?? [], totalMl),
    lobe: buildLobeQfv(subjectId, atlasStats.lobe ?? [], totalMl),
    aspects: buildAspectsQfv(subjectId, atlasStats.aspects ?? [], totalMl),
  };
}

// ---------------------------------------------------------------------------
// ASPECTSPC / BPM_TYPE1 / VENTRICLES — the remaining `save_QFV_to_csv`
// outputs (`reporting_qfv_calculation.py`). Each follows its own native
// arithmetic, which is NOT the same across atlases:
//  - ASPECTSPC (`cal_aspectpc_prob` + `get_Aspectpc_visual_prob_comb`):
//    per-label probability, rounded to 7dp, then L/R pairs summed for the
//    first 6 labels; Pons/Midbrain passed through; rounded again.
//  - BPM_TYPE1 (`cal_bpm_prob` + `get_BPM_visual_prob_comb`): per-RAW-label
//    probability (all 268 labels), then SUMMED into the 89 visual ROIs,
//    rounded to 7dp, then combined to 46 and rounded again — a sum of
//    per-label ratios, so values can exceed 1 (native IF = 1.0639953,
//    Ins = 2.2161322 for sub-02e8eb42 DWI). NOTE: this is the
//    `github_repo/farialab/OpenADS` version of `cal_bpm_prob`, which is the
//    one that produced the reference output. The newer copy in
//    `15_one4all_stages/OpenADS` (ratio of combined count / combined volume,
//    always <= 1) does NOT reproduce the reference CSVs (max abs diff 1.46
//    on DWI, checked by running that Python against the same mask) — if
//    native switches to it, swap `buildBpmType1Qfv`'s arithmetic accordingly.
//  - VENTRICLES (`cal_ventricles_prob`): `1 - LVO/LVOR` for LVOR1..10 and
//    `LVI/LVIR` for LVIR1..10; passed through `get_QFV` unchanged.
// Lesionload column names are `names.json`'s `LabelLookupTable` (carried
// in each `RegionStat.region` / `VentricleFeatures.regions`).
// ---------------------------------------------------------------------------

/** Python `repr(float)` formatting, which is what pandas' `to_csv` writes
 *  for float columns: `0.0`/`1.0` for integral values, and `2.04e-05`-style
 *  exponent notation below 1e-4 (JS switches at 1e-6 and drops the
 *  exponent's zero-padding). Used for the QFV cells of the CSVs below so
 *  they are byte-identical to native, not just numerically equal. */
function pyFloat(v: number): string {
  if (!Number.isFinite(v)) return Number.isNaN(v) ? "nan" : v > 0 ? "inf" : "-inf";
  if (Number.isInteger(v) && Math.abs(v) < 1e16) return `${v}.0`;
  const a = Math.abs(v);
  if (a < 1e-4 || a >= 1e16) {
    return v.toExponential().replace(/e([+-])(\d)$/, "e$10$2");
  }
  return String(v);
}

const ASPECTPC_QFV_NAMES = ["PCA", "Thalamus", "Cerebellum", "Pons", "Midbrain"];

export function buildAspectsPcQfv(subjectId: string, stats: RegionStat[], totalMl: number): QfvCsvSet {
  const prob8 = stats.map((s) => round7(s.overlapVoxels / (s.regionTotalVoxels + 1e-6)));
  const combined: number[] = [];
  for (let i = 0; i + 1 < prob8.length - 2; i += 2) combined.push(prob8[i] + prob8[i + 1]);
  combined.push(...prob8.slice(-2));
  const qfvCsv = `${csvRow(["subject_id", "volml", ...ASPECTPC_QFV_NAMES])}\n${csvRow([subjectId, pyFloat(volmlColumn(totalMl)), ...combined.map((v) => pyFloat(round7(v)))])}\n`;
  return { qfvCsv, lesionloadCsv: lesionload(subjectId, stats) };
}

/** `vec_BPMAtlas2visual`: 268 BPM Type-I labels -> 89 visual ROIs, ported exact. */
const BPM_VISUAL89: string[][] = [
  ["SFG_L", "SFWM_L"], ["SFG_R", "SFWM_R"],
  ["SFG_PFC_L", "SFWM_PFC_L"], ["SFG_PFC_R", "SFWM_PFC_R"],
  ["SFG_pole_L", "SFWM_pole_L"], ["SFG_pole_R", "SFWM_pole_R"],
  ["MFG_L", "MFWM_L"], ["MFG_R", "MFWM_R"],
  ["MFG_DPFC_L", "MFWM_DPFC_L"], ["MFG_DPFC_R", "MFWM_DPFC_R"],
  ["IFG_opercularis_L", "IFG_orbitalis_L", "IFG_triangularis_L", "IFWM_opercularis_L", "IFWM_orbitalis_L", "IFWM_triangularis_L"],
  ["IFG_opercularis_R", "IFG_orbitalis_R", "IFG_triangularis_R", "IFWM_opercularis_R", "IFWM_orbitalis_R", "IFWM_triangularis_R"],
  ["LFOG_L", "MFOG_L", "RG_L", "LFOWM_L", "MFOWM_L", "RGWM_L"],
  ["LFOG_R", "MFOG_R", "RG_R", "LFOWM_R", "MFOWM_R", "RGWM_R"],
  ["PoCG_L", "PoCWM_L"], ["PoCG_R", "PoCWM_R"],
  ["PrCG_L", "PrCWM_L"], ["PrCG_R", "PrCWM_R"],
  ["SPG_L", "SPWM_L"], ["SPG_R", "SPWM_R"],
  ["SMG_L", "SMWM_L"], ["SMG_R", "SMWM_R"],
  ["AG_L", "AWM_L"], ["AG_R", "AWM_R"],
  ["PrCu_L", "PrCuWM_L"], ["PrCu_R", "PrCuWM_R"],
  ["STG_L", "STWM_L"], ["STG_R", "STWM_R"],
  ["STG_L_pole", "STWM_L_pole"], ["STG_R_pole", "STWM_R_pole"],
  ["MTG_L", "MTWM_L"], ["MTG_R", "MTWM_R"],
  ["MTG_L_pole", "MTWM_L_pole"], ["MTG_R_pole", "MTWM_R_pole"],
  ["ITG_L", "ITWM_L"], ["ITG_R", "ITWM_R"],
  ["PHG_L", "ENT_L", "Amyg_L", "Hippo_L", "CGH_L"],
  ["PHG_R", "ENT_R", "Amyg_R", "Hippo_R", "CGH_R"],
  ["FuG_L", "FuWM_L"], ["FuG_R", "FuWM_R"],
  ["SOG_L", "SOWM_L"], ["SOG_R", "SOWM_R"],
  ["MOG_L", "MOWM_L"], ["MOG_R", "MOWM_R"],
  ["IOG_L", "IOWM_L"], ["IOG_R", "IOWM_R"],
  ["Cu_L", "CuWM_L"], ["Cu_R", "CuWM_R"],
  ["LG_L", "LWM_L"], ["LG_R", "LWM_R"],
  ["rostral_ACC_L", "subcallosal_ACC_L", "subgenual_ACC_L", "dorsal_ACC_L", "PCC_L", "CGC_L", "rostralWM_ACC_L", "subcallosalWM_ACC_L", "subgenualWM_ACC_L", "dorsalWM_ACC_L", "PCCWM_L"],
  ["rostral_ACC_R", "subcallosal_ACC_R", "subgenual_ACC_R", "dorsal_ACC_R", "PCC_R", "CGC_R", "rostralWM_ACC_R", "subcallosalWM_ACC_R", "subgenualWM_ACC_R", "dorsalWM_ACC_R", "PCCWM_R"],
  ["Ins_L", "EC_L", "Pins_L"], ["Ins_R", "EC_R", "Pins_R"],
  ["Caud_L"], ["Caud_R"],
  ["Put_L"], ["Put_R"],
  ["GP_L"], ["GP_R"],
  ["Thal_L"], ["Thal_R"],
  ["HypoThalamus_L", "HypoThalamus_R", "Mynert_L", "Mynert_R", "NucAccumbens_L", "NucAccumbens_R", "RedNc_L", "RedNc_R", "Snigra_L", "Snigra_R", "CP_L", "CP_R", "Midbrain_L", "Midbrain_R", "CST_L", "CST_R"],
  ["Cerebellum_L", "SCP_L", "MCP_L", "PCT_L", "ICP_L", "CerebellumWM_L"],
  ["Cerebellum_R", "SCP_R", "MCP_R", "PCT_R", "ICP_R", "CerebellumWM_R"],
  ["ML_L", "ML_R", "Pons_L", "Pons_R", "Medulla_L", "Medulla_R"],
  ["ACR_L"], ["ACR_R"],
  ["SCR_L"], ["SCR_R"],
  ["PCR_L"], ["PCR_R"],
  ["GCC_L", "GCC_R", "BCC_L", "BCC_R", "SCC_L", "SCC_R"],
  ["ALIC_L", "PLIC_L", "RLIC_L"], ["ALIC_R", "PLIC_R", "RLIC_R"],
  ["Fx/ST_L", "PTR_L", "SS_L"], ["Fx/ST_R", "PTR_R", "SS_R"],
  ["IFO_L"], ["IFO_R"],
  ["SFO_L", "SLF_L"], ["SFO_R", "SLF_R"],
  ["UNC_L"], ["UNC_R"],
  ["PSTG_L", "PSTGWM_L"], ["PSTG_R", "PSTGWM_R"],
  ["PMTG_L", "PMTGWM_L"], ["PMTG_R", "PMTGWM_R"],
  ["PITG_L", "PITGWM_L"], ["PITG_R", "PITGWM_R"],
];

/** `get_BPM_visual_prob_comb`: 89 -> 46, L/R pairs except the single
 *  already-bilateral midbrain (62), pons (65) and corpus callosum (72). */
const BPM_SINGLETONS = new Set([62, 65, 72]);
function bpmComb(v89: number[]): number[] {
  const out: number[] = [];
  let i = 0;
  while (i < v89.length) {
    if (BPM_SINGLETONS.has(i)) { out.push(v89[i]); i += 1; }
    else { out.push(v89[i] + v89[i + 1]); i += 2; }
  }
  return out;
}

const BPM_QFV_NAMES = [
  "SF", "SF_PF", "SF_pole", "MF", "MF_DPF", "IF", "FO", "PoC", "PrC", "SP", "SM", "AG", "PrCu", "ST", "ST_pole",
  "MT", "MT_pole", "IT", "Limbic", "Fu", "SO", "MO", "IO", "Cu", "Ling", "Cing", "Ins", "Caud", "Put", "GP", "Thal",
  "midb", "cereb", "pons", "ACR", "SCR", "PCR", "CC", "IC", "PTR", "IFOF", "SLF", "Unc", "PST", "PMT", "PIT",
];

export function buildBpmType1Qfv(subjectId: string, stats: RegionStat[], totalMl: number): QfvCsvSet {
  const probByName = byName(stats, (s) => s.overlapVoxels / (s.regionTotalVoxels + 1e-6));
  const prob89 = BPM_VISUAL89.map((names) => round7(names.reduce((a, n) => a + (probByName.get(n) ?? 0), 0)));
  const prob46 = bpmComb(prob89);
  const qfvCsv = `${csvRow(["subject_id", "volml", ...BPM_QFV_NAMES])}\n${csvRow([subjectId, pyFloat(volmlColumn(totalMl)), ...prob46.map((v) => pyFloat(round7(v)))])}\n`;
  return { qfvCsv, lesionloadCsv: lesionload(subjectId, stats) };
}

/** The 20 VENTRICLES QFV values (LVOR1..10 then LVIR1..10), exactly as
 *  written to the CSV — also the hydrocephalus model's features. */
export function ventriclesQfvValues(vent: VentricleFeatures): number[] {
  const lvo = vent.lvoCounts.map((c, i) => round7(1 - c / (vent.lvorVolumeVoxels[i] + 1e-6)));
  const lvi = vent.lviCounts.map((c, i) => round7(c / (vent.lvirVolumeVoxels[i] + 1e-6)));
  return [...lvo, ...lvi];
}

/** The `volml` cell every QFV CSV carries (log10 mL, see header comment). */
export function qfvVolml(totalMl: number): number {
  return volmlColumn(totalMl);
}

export function buildVentriclesQfv(subjectId: string, vent: VentricleFeatures, totalMl: number): QfvCsvSet {
  const qfvCsv = `${csvRow(["subject_id", "volml", ...vent.regions])}\n${csvRow([subjectId, pyFloat(volmlColumn(totalMl)), ...ventriclesQfvValues(vent).map(pyFloat)])}\n`;
  const lesionloadCsv = `${csvRow(vent.regions)}\n${csvRow([...vent.lvoCounts, ...vent.lviCounts])}\n`;
  return { qfvCsv, lesionloadCsv };
}
